/**
 * @description Merchant Reporting v1 — Transaction tab on paid-invoice facts (LYN-243).
 * @dependencies reportingService, reportingDatePresets, FinancialReports, PermissionContext
 * @notes Grain is paid line totals excluding shipping/discount/tax.
 * Charts follow Untitled UI line/bar patterns (Recharts + Tailwind).
 * Period-point drill uses week/month inclusive ranges clamped to the filter.
 * SO Reporting stays on /so/transactions. Financial tab stays hidden without
 * reports.view_financial.
 * Page chrome (title + Export) matches other main-nav pages: max-w-[2000px]
 * wrapper, text-2xl font-bold title, brand primary-action button classes.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatCurrency } from '../utils/invoiceHelpers';
import {
  CHART,
  ChartCard,
  ChartEmpty,
  ReportingBarChart,
  ReportingLineChart
} from './reporting/ReportingCharts';
import { getCustomerDisplayName } from '../utils/customerHelpers';
import { customersService } from '../services/api';
import reportingService from '../services/api/reportingService';
import FinancialReports from './FinancialReports';
import { usePermissionsSafe } from '../contexts/PermissionContext';
import { PERMISSIONS } from '../constants/permissionMappings';
import {
  REPORTING_DATE_PRESETS,
  resolveDatePreset,
  buildPaidInvoiceDrilldownPath,
  periodDrilldownRange
} from '../utils/reportingDatePresets';

function centsToDollars(cents) {
  return (Number(cents) || 0) / 100;
}

/**
 * Middleware 403s return `{ error: { message } }`; ReportingService 400s return a string.
 * Never pass an object into React text state.
 * @param {unknown} err
 * @returns {string}
 */
function loadReportsErrorMessage(err) {
  const payload = err?.response?.data?.error;
  if (typeof payload === 'string' && payload.trim()) return payload;
  if (payload && typeof payload.message === 'string' && payload.message.trim()) {
    return payload.message;
  }
  return err?.message || 'Failed to load reports';
}

function formatPeriodLabel(periodStart, period) {
  if (!periodStart) return '';
  const date = new Date(periodStart);
  if (Number.isNaN(date.getTime())) return String(periodStart).slice(0, 10);
  if (period === 'month') {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  if (period === 'week') {
    return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function Reporting({ user }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { hasPermission, isLoaded } = usePermissionsSafe();
  const canViewFinancial = isLoaded && hasPermission(PERMISSIONS.reports.view_financial);
  const initialRange = resolveDatePreset('month');
  const [activeTab, setActiveTab] = useState('Transaction Reports');
  const [filters, setFilters] = useState({
    customerId: '',
    dateFrom: initialRange.dateFrom,
    dateTo: initialRange.dateTo,
    datePreset: 'month'
  });
  const [customers, setCustomers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [byPeriod, setByPeriod] = useState([]);
  const [byProduct, setByProduct] = useState([]);
  const [periodGrain, setPeriodGrain] = useState('day');
  const [productMetric, setProductMetric] = useState('dollars');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCustomerOpen, setIsCustomerOpen] = useState(false);
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [tempDates, setTempDates] = useState({ from: '', to: '' });
  const customerRef = useRef(null);
  const dateRef = useRef(null);

  useEffect(() => {
    const onDocClick = (event) => {
      if (customerRef.current && !customerRef.current.contains(event.target)) {
        setIsCustomerOpen(false);
      }
      if (dateRef.current && !dateRef.current.contains(event.target)) {
        setIsDateOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    customersService.getMerchantCustomers()
      .then((list) => setCustomers(Array.isArray(list) ? list : []))
      .catch(() => setCustomers([]));
  }, [user?.id]);

  const loadReports = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    const queryParams = {
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      customerId: filters.customerId || undefined
    };
    try {
      const [summaryData, periodData, productData] = await Promise.all([
        reportingService.getSalesSummary(queryParams),
        reportingService.getSalesByPeriod(queryParams),
        reportingService.getSalesByProduct(queryParams)
      ]);
      setSummary(summaryData);
      setByPeriod(periodData?.rows || []);
      setPeriodGrain(periodData?.period || 'day');
      setByProduct(productData?.rows || []);
    } catch (err) {
      setError(loadReportsErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [user?.id, filters.dateFrom, filters.dateTo, filters.customerId]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    if (!canViewFinancial && activeTab === 'Financial Reports') {
      setActiveTab('Transaction Reports');
    }
  }, [canViewFinancial, activeTab]);

  const applyPreset = (preset) => {
    const range = resolveDatePreset(preset);
    setFilters((prev) => ({ ...prev, ...range, datePreset: preset }));
    setIsDateOpen(false);
  };

  const selectedCustomer = customers.find((c) => String(c.id) === String(filters.customerId));

  const exportCsv = () => {
    const headers = ['Period', 'Invoices', 'Quantity sold', 'Sales cents'];
    const rows = byPeriod.map((row) => [
      String(row.periodStart || '').slice(0, 10),
      row.invoiceCount,
      row.quantitySold,
      row.totalSalesCents
    ]);
    const productHeaders = ['Product', 'Bucket', 'Quantity sold', 'Sales cents'];
    const productRows = byProduct.map((row) => [
      `"${(row.productName || '').replace(/"/g, '""')}"`,
      row.bucket,
      row.quantitySold,
      row.totalSalesCents
    ]);
    const csv = [
      'Sales by period',
      headers.join(','),
      ...rows.map((r) => r.join(',')),
      '',
      'Sales by product',
      productHeaders.join(','),
      ...productRows.map((r) => r.join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transaction-report-${filters.dateFrom || 'all'}-to-${filters.dateTo || 'all'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const drillToInvoices = (dateFrom, dateTo) => {
    navigate(buildPaidInvoiceDrilldownPath({
      dateFrom,
      dateTo,
      customerId: filters.customerId || null
    }, pathname));
  };

  const periodChartData = byPeriod.map((row) => ({
    ...row,
    label: formatPeriodLabel(row.periodStart, periodGrain),
    dollars: centsToDollars(row.totalSalesCents)
  }));

  const productChartData = byProduct.slice(0, 8).map((row) => ({
    ...row,
    label: row.productName,
    value: productMetric === 'units' ? Number(row.quantitySold) || 0 : centsToDollars(row.totalSalesCents)
  }));

  return (
    <div className="max-w-[2000px] w-full mx-auto px-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Reporting</h1>
        {activeTab === 'Transaction Reports' && (
          <button
            type="button"
            onClick={exportCsv}
            disabled={loading || (byPeriod.length === 0 && byProduct.length === 0)}
            className="bg-brand text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-brand-hover transition-colors focus:outline-none disabled:opacity-50"
          >
            Export CSV
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {['Transaction Reports', ...(canViewFinancial ? ['Financial Reports'] : [])].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
              activeTab === tab
                ? 'border-brand text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {canViewFinancial && activeTab === 'Financial Reports' ? (
        <FinancialReports user={user} />
      ) : (
        <>
          <div className="flex flex-wrap gap-3 mb-6">
            <div className="relative" ref={customerRef}>
              <button
                type="button"
                onClick={() => setIsCustomerOpen((open) => !open)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-semibold min-w-[180px] text-left"
              >
                {selectedCustomer ? getCustomerDisplayName(selectedCustomer) : 'All Customers'}
              </button>
              {isCustomerOpen && (
                <div className="absolute z-10 mt-1 w-64 bg-white rounded-md shadow-lg py-1 max-h-60 overflow-auto">
                  <button
                    type="button"
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                    onClick={() => {
                      setFilters((prev) => ({ ...prev, customerId: '' }));
                      setIsCustomerOpen(false);
                    }}
                  >
                    All Customers
                  </button>
                  {customers.map((customer) => (
                    <button
                      key={customer.id}
                      type="button"
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      onClick={() => {
                        setFilters((prev) => ({ ...prev, customerId: customer.id }));
                        setIsCustomerOpen(false);
                      }}
                    >
                      {getCustomerDisplayName(customer)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={dateRef}>
              <button
                type="button"
                onClick={() => setIsDateOpen((open) => !open)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-semibold min-w-[160px] text-left"
              >
                {filters.datePreset === 'custom' ? 'Custom Dates' : (filters.datePreset ? filters.datePreset.charAt(0).toUpperCase() + filters.datePreset.slice(1) : 'Dates')}
              </button>
              {isDateOpen && (
                <div className="absolute z-10 mt-1 w-48 bg-white rounded-md shadow-lg py-1">
                  {REPORTING_DATE_PRESETS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      onClick={() => applyPreset(preset)}
                    >
                      {preset.charAt(0).toUpperCase() + preset.slice(1)}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                    onClick={() => {
                      setTempDates({ from: filters.dateFrom, to: filters.dateTo });
                      setShowCustomDates(true);
                      setIsDateOpen(false);
                    }}
                  >
                    Custom Dates
                  </button>
                </div>
              )}
            </div>

            {(filters.dateFrom || filters.dateTo) && (
              <div className="flex items-center text-sm text-gray-600">
                {filters.dateFrom} to {filters.dateTo}
              </div>
            )}
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <SummaryCard
              label="Total sales"
              value={formatCurrency(centsToDollars(summary?.totalSalesCents))}
              loading={loading}
            />
            <SummaryCard
              label="Paid invoices"
              value={summary ? String(summary.invoiceCount || 0) : '—'}
              loading={loading}
            />
            <SummaryCard
              label="# sold"
              value={summary ? String(summary.quantitySold || 0) : '—'}
              loading={loading}
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <ChartCard
              title="Sales by Period"
              subtitle={periodGrain === 'day' ? 'Daily paid sales' : periodGrain === 'week' ? 'Weekly paid sales' : 'Monthly paid sales'}
            >
              {periodChartData.length === 0 && !loading ? (
                <ChartEmpty text="No paid sales in this range." />
              ) : (
                <ReportingLineChart
                  data={periodChartData}
                  dataKey="dollars"
                  name="Sales"
                  onPointClick={(row) => {
                    if (!row?.periodStart) return;
                    const range = periodDrilldownRange(row.periodStart, periodGrain, {
                      dateFrom: filters.dateFrom,
                      dateTo: filters.dateTo
                    });
                    if (!range.dateFrom) return;
                    drillToInvoices(range.dateFrom, range.dateTo);
                  }}
                />
              )}
            </ChartCard>

            <ChartCard
              title="Sales by Product"
              subtitle={byProduct.length > 8 ? 'Top 8 by sales' : 'Paid line totals'}
              action={(
                <div className="flex overflow-hidden rounded-lg border border-gray-200 text-sm">
                  <button
                    type="button"
                    className={`px-3 py-1 ${productMetric === 'dollars' ? 'bg-brand text-white' : 'bg-white text-gray-700'}`}
                    onClick={() => setProductMetric('dollars')}
                  >
                    $
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1 ${productMetric === 'units' ? 'bg-brand text-white' : 'bg-white text-gray-700'}`}
                    onClick={() => setProductMetric('units')}
                  >
                    #
                  </button>
                </div>
              )}
            >
              {productChartData.length === 0 && !loading ? (
                <ChartEmpty text="No product lines in this range." />
              ) : (
                <ReportingBarChart
                  data={productChartData}
                  layout="bar"
                  valueFormatter={productMetric === 'units' ? (value) => String(value) : formatCurrency}
                  bars={[{ dataKey: 'value', name: productMetric === 'units' ? 'Units' : 'Sales', color: CHART.brand }]}
                />
              )}
            </ChartCard>
          </div>
        </>
      )}

      {showCustomDates && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Select Date Range</h3>
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                From
                <input
                  type="date"
                  value={tempDates.from}
                  onChange={(e) => setTempDates((prev) => ({ ...prev, from: e.target.value }))}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                To
                <input
                  type="date"
                  value={tempDates.to}
                  onChange={(e) => setTempDates((prev) => ({ ...prev, to: e.target.value }))}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </label>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-lg"
                onClick={() => setShowCustomDates(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-brand text-white rounded-lg font-semibold"
                onClick={() => {
                  setFilters((prev) => ({
                    ...prev,
                    dateFrom: tempDates.from,
                    dateTo: tempDates.to,
                    datePreset: 'custom'
                  }));
                  setShowCustomDates(false);
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, loading }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{loading ? '…' : value}</div>
    </div>
  );
}

export { loadReportsErrorMessage };
export default Reporting;
